import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { useHistory } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import { BreadCrumb } from "../../../components/Common";
import {
  PaginateQuery,
  State,
  TeacherAdminDto,
  SubjectDto,
} from "../../../interfaces";
import {
  deleteTeacherAdminUser,
  deleteTeacherAdminUserConsent,
  getTeacherAdminUsers,
  getCourseAndGradesSelectList,
} from "../../../store/actions";
import { Paginated, SelectListDto } from "../../../models";
import {
  ActionButton,
  AppTable,
  ColumnHeader,
  DateColumn,
  HTMLColumn,
  SubjectsSelect,
  TableSpinner,
  TextColumn,
} from "../../../components";
import { ActionTypeEnum, RoleEnum } from "../../../enums";
import DeleteModal from "../../../components/Common/DeleteModal";
import { AddEditAdminTeacherModal } from "./AddEditAdminTeacherModal";

type TProps = {};
export const AdminTeachersPage: React.FC<TProps> = () => {
  //#region hooks

  const history = useHistory();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region redux state

  const { loading, response } = useSelector(
    (state: any): State<Paginated<TeacherAdminDto>> =>
      state.teacherAdminUser.teacherAdminUsers
  );
  const {
    loading: deleteTeacherAdminUserLoading,
    success: deleteTeacherAdminUserSuccess,
    error: deleteTeacherAdminUserError,
  } = useSelector(
    (state: any): State<TeacherAdminDto> =>
      state.teacherAdminUser.deleteTeacherAdminUser
  );

  useSelector(
    (state: any): State<SubjectDto[]> => state.subject.subjectsByGrade
  );
  //#endregion

  //#region internal state
  const initState = new PaginateQuery();

  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [filterSubjectId, setFilterSubjectId] = useState<string[]>([]);

  //#endregion

  document.title = `${t("documentTitle", { pageName: "cpUsers" })}`;

  const columns: TableColumn<TeacherAdminDto>[] = [
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => (
        <TextColumn text={row.name[i18n.language === "ar" ? "ar" : "en"]} />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="subjects" />,
      cell: (row) => <HTMLColumn html={getSubjectsName(row.subjects)} />,
      sortable: false,
    },
    {
      name: <ColumnHeader title="phoneNumber" />,
      cell: (row) => (
        <a
          style={{ cursor: "pointer" }}
          href={`tel:+${row.phoneObj?.countryCode}${row.phoneObj?.phoneNumber}`}
        >
          <u>{`+${row.phoneObj?.countryCode}${row.phoneObj?.phoneNumber}`}</u>
        </a>
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      width: "120px",
      cell: (row) => (
        <React.Fragment>
          <ActionButton
            onAction={() => onEdit(row.id)}
            actionType={ActionTypeEnum.EDIT}
          />
          &nbsp;
          <ActionButton
            onAction={() => onDelete(row.id)}
            actionType={ActionTypeEnum.DELETE}
          />
        </React.Fragment>
      ),
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "name.en":
      case "name.ar":
        sortBy = 1;
        break;
      default:
        sortBy = 2;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("created_at:DESC")
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  //#region effects

  useEffect(() => {
    if (!deleteTeacherAdminUserLoading && deleteTeacherAdminUserSuccess) {
      setDeletingItemId(null);
      setShowDeleteModal(false);
      dispatch(
        getTeacherAdminUsers(RoleEnum.TEACHER_ADMIN, paginateQuery, history)
      );
    }
  }, [deleteTeacherAdminUserLoading]);

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
      filter: queryParams.has("filter[subject]")
        ? {
            ["subject"]: queryParams.get("filter[subject]") || "",
          }
        : paginateQuery.filter,
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC"
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string)
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    if (query.filter && query.filter["subject"]) {
      setFilterSubjectId(query.filter["subject"] as string[]);
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
  }, []);

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy, filter } = paginateQuery;
      let query;
      if (page) query = `?page=${page}`;
      if (limit) query += `&limit=${limit}`;
      if (search && search !== "") {
        history.replace({
          search: `&search=${search}`,
        });
      }
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      if (
        filter &&
        Object.keys(filter).length &&
        Object.keys(filter).length > 0
      ) {
        let filters: string = "";
        Object.keys(filter).map((key: string) => {
          if (filter[key]) filters += `&filter[${key}]=${filter[key]}`;
        });
        query += filters;
      }
      history.replace({ search: query });
      dispatch(
        getTeacherAdminUsers(RoleEnum.TEACHER_ADMIN, paginateQuery, history)
      );
    }
  }, [paginateQuery]);

  useEffect(() => {
    dispatch(getCourseAndGradesSelectList());
  }, []);

  //#endregion

  //#region handlers

  const onAdd = () => {
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingItemId(id);
    setShowAddEditModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
    dispatch(deleteTeacherAdminUserConsent(id));
  };

  const onDeleteConfirm = () => {
    dispatch(deleteTeacherAdminUser(deletingItemId as string, history));
    setDeletingItemId(null);
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "name" + "." + i18n.language;
        break;
      default:
        sortBy = "created_at";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<TeacherAdminDto>,
    sortDirection: string
  ) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  const onChangeSubjectFilter = (selectedValue: any) => {
    const subjectFilter: string[] = selectedValue.map((s: any) => s.value);
    setFilterSubjectId(subjectFilter);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["subject"]: subjectFilter,
      },
    });
  };

  const getSubjectsName = (subjects: SelectListDto[]) => {
    let _sub = subjects;
    if (_sub.length > 8) _sub.splice(7);
    if (i18n.language === "ar") {
      return _sub
        .map(
          (item: SelectListDto) =>
            `<span class="badge bg-dark text-light">${item.value.ar}</span>`
        )
        .join("<br/>");
    } else {
      return _sub
        .map(
          (item: SelectListDto) =>
            `<span class="badge bg-dark text-light">${item.value.en}</span>`
        )
        .join("<br/>");
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("teachers")} pageTitle={t("cpUsers")} />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <SubjectsSelect
                        placeholder="chooseSubject"
                        isMulti={true}
                        onChange={(value: any) => onChangeSubjectFilter(value)}
                        value={undefined}
                      ></SubjectsSelect>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button color="success" onClick={() => onAdd()}>
                          <i className="ri-add-fill me-1 align-bottom"></i>
                          {t("addItem", { item: "users" })}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <AppTable
                        theme={"dark"}
                        columns={columns}
                        data={response?.data || []}
                        progressPending={loading}
                        progressComponent={<TableSpinner />}
                        pagination
                        paginationServer
                        sortServer
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        persistTableHead
                        paginationTotalRows={response?.meta?.totalItems}
                        paginationComponentOptions={{
                          rowsPerPageText: t("rowsPerPage"),
                          rangeSeparatorText: t("of"),
                        }}
                        fixedHeader={true}
                        fixedHeaderScrollHeight="60vh"
                      />
                    </Col>
                  </Row>
                  <DeleteModal
                    loading={deleteTeacherAdminUserLoading}
                    show={showDeleteModal}
                    onDeleteClick={() => onDeleteConfirm()}
                    onCloseClick={() => {
                      setShowDeleteModal(false);
                    }}
                    error={deleteTeacherAdminUserError}
                  />
                  <AddEditAdminTeacherModal
                    id={editingItemId}
                    show={showAddEditModal}
                    onCloseClick={() => {
                      setShowAddEditModal(false);
                      dispatch(
                        getTeacherAdminUsers(
                          RoleEnum.TEACHER_ADMIN,
                          paginateQuery,
                          history
                        )
                      );
                      setEditingItemId(null);
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};
